import React, { createContext, useContext, useRef } from 'react';

interface ScrollPosition {
  offset: number;
  timestamp: number;
}

interface ScrollPositionContextValue {
  getScrollPosition: (key: string) => ScrollPosition | undefined;
  setScrollPosition: (key: string, offset: number) => void;
  clearScrollPosition: (key: string) => void;
  clearAllScrollPositions: () => void;
}

const ScrollPositionContext = createContext<
  ScrollPositionContextValue | undefined
>(undefined);

interface ScrollPositionProviderProps {
  children: React.ReactNode;
}

export function ScrollPositionProvider({
  children,
}: ScrollPositionProviderProps) {
  const scrollPositionsRef = useRef<Map<string, ScrollPosition>>(new Map());

  const getScrollPosition = (key: string): ScrollPosition | undefined => {
    return scrollPositionsRef.current.get(key);
  };

  const setScrollPosition = (key: string, offset: number): void => {
    scrollPositionsRef.current.set(key, {
      offset,
      timestamp: Date.now(),
    });
  };

  const clearScrollPosition = (key: string): void => {
    scrollPositionsRef.current.delete(key);
  };

  const clearAllScrollPositions = (): void => {
    scrollPositionsRef.current.clear();
  };

  const value: ScrollPositionContextValue = {
    getScrollPosition,
    setScrollPosition,
    clearScrollPosition,
    clearAllScrollPositions,
  };

  return (
    <ScrollPositionContext.Provider value={value}>
      {children}
    </ScrollPositionContext.Provider>
  );
}

export function useScrollPosition(): ScrollPositionContextValue {
  const context = useContext(ScrollPositionContext);
  if (context === undefined) {
    throw new Error(
      'useScrollPosition must be used within a ScrollPositionProvider',
    );
  }
  return context;
}
